// =============================================================================
// 📁 File: src/api/ddoApi.ts
// =============================================================================
// CTO Recovery Prompt — Canonical DDO API client (Frontend → Backend contract)
//
// Invariants:
// - Uses canonical axiosInstance (auth + tenant + refresh)
// - No stubs. All functions talk to real endpoints.
// - Aligned with backend schemas: app/schemas/ddo_schemas.py
// - No duplication of policy or role logic (server is source of truth)
// =============================================================================

import axiosInstance from "@/utils/axiosInstance";

// -----------------------------------------------------------------------------
// 📊 Dashboard (GET /ddo/dashboard)
// -----------------------------------------------------------------------------

/**
 * Matches backend DDODashboardResponse
 * (app.schemas.ddo_schemas.DDODashboardResponse)
 */
export interface DDODashboardStats {
  assignedToMe: number;
  myTotalApplications: number;
  awaitingMyAction: number;
  staleAssignments: number;
  lockedApplications: number;
  totalApplicationsDisbursed: number;
  upcomingInterviews: number;
  mentorshipLogs: number;
  assignedRegions: string[];

  // Optional extras from backend
  statusBreakdown?: Record<string, number>;
  trendView?: Array<Record<string, string | number>>;
}

/**
 * GET /ddo/dashboard
 * Query params:
 *   start_date?: yyyy-MM-dd
 *   end_date?: yyyy-MM-dd
 */
export async function fetchDDODashboard(params?: {
  start_date?: string;
  end_date?: string;
  program_code?: string;
  academic_year?: number;
  applicant_name?: string;
}): Promise<DDODashboardStats> {
  const res = await axiosInstance.get<DDODashboardStats>("/ddo/dashboard", {
    params,
  });
  return res.data;
}

// -----------------------------------------------------------------------------
// 📁 Queue (GET /ddo/submissions)
// Minimal paginated surface with URL-queryable filters
// -----------------------------------------------------------------------------

/**
 * Matches backend DDOQueueItem
 */
export interface DDOQueueItem {
  id: number;
  status: string | null;
  program_code: string | null;
  academic_year: number | null;
  student_id: number | null;
  assigned_at: string | null;
  assignment_state: "assigned" | "stale" | "unassigned" | null;
  assignment_age_days: number | null;
  locked: boolean | null;
  lifecycle_phase:
    | "application_review"
    | "interview"
    | "mentorship"
    | "management_review"
    | "finance_review"
    | "disbursal"
    | "closed";
  actionability_state:
    | "actionable"
    | "waiting_external"
    | "waiting_internal"
    | "blocked"
    | "locked"
    | "completed"
    | null;
  blocking_reason: string | null;
  executable_action_count: number;
  review_state:
    | "new_submission"
    | "under_review"
    | "clarification_requested"
    | "resubmitted"
    | "returned_backward"
    | "rejected"
    | "completed"
    | null;
  latest_governance_event: string | null;
  latest_governance_actor: string | null;
  latest_governance_reason: string | null;
  latest_governance_phase: string | null;
  latest_return_source: string | null;
}

/**
 * Matches backend DDOQueueResponse
 */
export interface DDOQueueResponse {
  page: number;
  page_size: number;
  total: number;
  items: DDOQueueItem[];
}

/**
 * Query params for GET /ddo/submissions
 */
export interface DDOQueueParams {
  page?: number;
  page_size?: number;
  status?: string | null;
  program_code?: string | null;
  academic_year?: number | null;
  applicant_name?: string | null;
  date_from?: string | null; // yyyy-MM-dd
  date_to?: string | null;   // yyyy-MM-dd
  include_locked?: boolean;
  include_deleted?: boolean;
  actionable_only?: boolean;
  stale_only?: boolean;
  locked_only?: boolean;
}

export interface DDOBulkAvailableAction {
  action_code: string;
  to_status: string;
}

export interface DDOAvailableAction {
  action_code: string;
  to_status: string;
}

export interface DDOAvailableActionsResponse {
  submission_id: number;
  current_state: string | null;
  actions: DDOAvailableAction[];
}

export interface DDOBulkSubmissionActionDiscovery {
  submission_id: number;
  actions: DDOBulkAvailableAction[];
  error?: string | null;
}

export interface DDOBulkAvailableActionsResponse {
  total: number;
  common_actions: DDOBulkAvailableAction[];
  submissions: DDOBulkSubmissionActionDiscovery[];
}

export interface DDOBulkTransitionResult {
  submission_id: number;
  status: "ok" | "error";
  to_status?: string | null;
  error?: string | null;
}

export interface DDOBulkTransitionResponse {
  total: number;
  success: number;
  failure: number;
  results: DDOBulkTransitionResult[];
}

/**
 * GET /ddo/submissions (simple queue list)
 */
export async function fetchDDOQueue(
  params: DDOQueueParams = {}
): Promise<DDOQueueResponse> {
  const res = await axiosInstance.get<DDOQueueResponse>("/ddo/submissions", {
    params,
  });
  return res.data;
}

export async function fetchDDOAvailableActions(
  submissionId: number
): Promise<DDOAvailableActionsResponse> {
  const res = await axiosInstance.get<DDOAvailableActionsResponse>(
    `/ddo/submissions/${submissionId}/available-actions`
  );
  return res.data;
}

export async function fetchDDOBulkAvailableActions(
  submissionIds: number[]
): Promise<DDOBulkAvailableActionsResponse> {
  const res = await axiosInstance.post<DDOBulkAvailableActionsResponse>(
    "/ddo/submissions/bulk-available-actions",
    { submission_ids: submissionIds }
  );
  return res.data;
}

export async function submitDDOBulkTransition(payload: {
  submission_ids: number[];
  action_code: string;
  remarks?: string;
}): Promise<DDOBulkTransitionResponse> {
  const res = await axiosInstance.post<DDOBulkTransitionResponse>(
    "/ddo/submissions/bulk-transition",
    payload
  );
  return res.data;
}
